import _omit from 'lodash/omit';
import { FieldItem } from '@lib/interfaces/form';
import formStyles from '@lib/assets/styles/Form.module.scss';
import SelectPanel from '@lib/components/SelectPanel/SelectPanel';
import * as PanelOptions from '@lib/components/SelectPanel/options';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import AreaOption from '@lib/components/Select/components/AreaOption';
import AssetOption from '@lib/components/Select/components/AssetOption';
import CheckedOption from '@lib/components/Select/components/CheckedOption';
import ChipOption from '@lib/components/Select/components/ChipOption';
import CountryOption from '@lib/components/Select/components/CountryOption';
import SectorOption from '@lib/components/Select/components/SectorOption';
import SiteOption from '@lib/components/Select/components/SiteOption';
import UserOption from '@lib/components/Select/components/UserOption';
import ValidityHintOption from '@lib/components/Select/components/ValidityHintOption';

// SelectPanel option renderers → their react-select `Option` counterparts, so
// a field keeps its rich option rows when it moves into the filter modal.
const SELECT_OPTION_BY_PANEL_OPTION = new Map<unknown, unknown>([
  [PanelOptions.AreaOption, AreaOption],
  [PanelOptions.AssetOption, AssetOption],
  [PanelOptions.CheckedOption, CheckedOption],
  [PanelOptions.ChipOption, ChipOption],
  [PanelOptions.CountryOption, CountryOption],
  [PanelOptions.SectorOption, SectorOption],
  [PanelOptions.SiteOption, SiteOption],
  [PanelOptions.UserOption, UserOption],
  [PanelOptions.ValidityHintOption, ValidityHintOption],
]);

/**
 * Adapts an inline filter-row field for the stacked filter modal: full-width
 * column with the usual error space, and a `SelectPanel` (compact popover
 * trigger, only fits an inline row) swapped for a regular `Select`.
 */
export default function toModalFilterField(field: FieldItem): FieldItem {
  const componentProps = field.componentProps ?? {};
  const baseField: FieldItem = {
    ...field,
    formItemClassName: formStyles.formColumnFullWidth,
    hideErrorsSpace: false,
  };

  if (field.element !== SelectPanel) {
    return {
      ...baseField,
      // Inline rows shrink e.g. the DateRangePicker; use the form default.
      componentProps: _omit(componentProps, 'size'),
    };
  }

  const Option = SELECT_OPTION_BY_PANEL_OPTION.get(componentProps.renderOption);

  return {
    ...baseField,
    element: Select,
    componentProps: {
      defaultOptions: true,
      disabled: componentProps.disabled,
      disabledTooltipProps: componentProps.disabledTooltipProps,
      getLoadOptions: componentProps.getLoadOptions,
      isAsync: componentProps.isAsync,
      isMulti: componentProps.isMulti,
      isOptionDisabled: componentProps.isOptionDisabled,
      isSearchable: componentProps.searchable ?? true,
      maxMenuHeight: 200,
      // The open Modal scroll-locks the page with `body { position: fixed;
      // top: -scrollY }`, so a body-portaled `absolute` menu lands scrollY px
      // too high (over its own control). `fixed` positions it off the viewport.
      menuPosition: 'fixed',
      options: componentProps.options,
      usePortal: true,
      components: {
        ValueContainer: MultiSelectValueContainer,
        ...(Option ? { Option } : {}),
      } as unknown as SelectComponents,
    },
  };
}
